import * as Sentry from '@sentry/browser'
import livewireComponentId from '../global/livewireComponentId.js'

/**
 * The history block of one agenda point: how much of its past is on screen and
 * how it fetches more.
 *
 * A card arrives with its newest entry already rendered - cut off, but from the
 * server - and every entry it fetches on top is shown in full. The answer comes
 * with `skipRender()`, so nothing morphs: the meeting editor keeps its travelling
 * rich-text editor, its caret and whatever is typed into it while a neighbouring
 * card unfolds. The whole list is re-rendered rather than appended, because
 * entries arrive oldest first and a folded run of "no news" can span the boundary
 * between what was already shown and what is being added.
 *
 * Folding up takes the same way: it asks for the primed card again. That looks
 * like a detour - the markup was here a moment ago - but it is the only way the
 * SERVER learns that this point is folded up again, and the server is what
 * renders the point the next time anything on the page renders. A card that
 * folded up on its own would spring open again at the next render.
 */
// Every instance gets a number, so a broadcast can be told from its own echo.
let historyInstances = 0

// What a card is primed with: its newest entry, nothing else.
const PRIMED = 1

export default ({
  itemId,
  loaded = 0,
  total = 0,
  allLabelTemplate = '',
  errorTitle = 'Error',
  errorMessage = 'The history could not be loaded.',
  // Optional identity of the past this block shows. Two blocks with the same key
  // are two views of ONE past - the meeting editor renders a point in the agenda
  // list and on the beamer stage at the same time. Whichever of them steps back
  // hands the entries to the other, so the mode and the list never disagree about
  // how far a point has been read back, and the second one asks the server for
  // nothing it already has.
  syncKey = null
} = {}) => {
  // Only the id, never the $wire proxy: read out of reactive state it would be
  // wrapped a second time and swallow the call.
  let wireId = null
  const instanceId = ++historyInstances

  return {
    loaded,
    total,
    busy: false,

    init() {
      // Teleport aware: the meeting editor renders a point in the agenda list AND
      // on the beamer stage, and the stage hangs on the body - where no ancestor
      // carries a `wire:id` any more.
      wireId = livewireComponentId(this.$root)
    },

    get hasMore() {
      return this.loaded < this.total
    },

    /** Unfolded means: more than the entry the card was primed with is on screen. */
    get expanded() {
      return this.loaded > PRIMED
    },

    get allLabel() {
      return allLabelTemplate.replace(':count', String(this.total))
    },

    loadMore() {
      return this.load(this.loaded + 1)
    },

    loadAll() {
      return this.load(0)
    },

    collapse() {
      return this.load(PRIMED)
    },

    /** Put a set of entries on screen. One place writes this block, not three. */
    showEntries(html, loadedCount, totalCount) {
      if (this.$refs.entries) this.$refs.entries.innerHTML = html

      this.loaded = loadedCount
      this.total = totalCount
    },

    /** Hand what is now shown to the other copy of this point. */
    broadcastEntries(html) {
      if (!syncKey) return

      window.dispatchEvent(new CustomEvent('point-history-synced', {
        detail: { key: syncKey, origin: instanceId, html, loaded: this.loaded, total: this.total }
      }))
    },

    /**
     * Adopt what the other copy fetched or folded up. Deliberately without a
     * request of its own: the entries are already here, and asking again would
     * only be a second answer to the same question.
     */
    adoptEntries(detail = {}) {
      if (!syncKey || detail.key !== syncKey || detail.origin === instanceId) return
      if (typeof detail.html !== 'string') return

      this.showEntries(detail.html, detail.loaded, detail.total)
    },

    /**
     * Resolve $wire freshly on every use: the element that triggered the click
     * can be gone by the time the answer arrives, and a stored magic would then
     * be Livewire's noop fallback - a call that resolves without ever asking.
     */
    async load(limit) {
      if (this.busy) return

      const wire = wireId && window.Livewire ? window.Livewire.find(wireId) : null

      if (!wire) {
        this.report(new Error('pointHistory: livewire component not resolvable'))
        return
      }

      this.busy = true

      try {
        const result = await wire.loadPointHistory(itemId, limit)

        if (!result || typeof result.html !== 'string') {
          throw new Error('pointHistory: unexpected answer')
        }

        this.showEntries(result.html, result.loaded, result.total)
        this.broadcastEntries(result.html)
      } catch (error) {
        this.report(error)
      } finally {
        this.busy = false
      }
    },

    report(error, message = errorMessage) {
      // The toast listener hangs on the window, and the button that started this
      // may already be gone - $dispatch would never reach it.
      window.dispatchEvent(new CustomEvent('notify', {
        detail: { type: 'danger', title: errorTitle, message }
      }))
      Sentry.captureException(error)
      console.error(error)
    }
  }
}
